import React, { useState } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { FaCalendarAlt, FaMapMarkerAlt, FaUsers, FaClock, FaExternalLinkAlt, FaBookmark, FaGraduationCap, FaHeartbeat, FaHandsHelping, FaCheckCircle, FaStar, FaTrophy } from 'react-icons/fa';

// Animation variants
const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};



const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.15,
            delayChildren: 0.1
        }
    }
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.5, ease: "easeOut" }
    }
};

const slideInFromBottom = {
    hidden: { opacity: 0, y: 50 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const EventsPage = () => {
    const [activeTab, setActiveTab] = useState('upcoming');
    const [selectedCategory, setSelectedCategory] = useState('All');

    const upcomingEvents = [
        {
            id: 1,
            title: "Annual Education Drive 2024",
            date: "2024-08-15",
            time: "09:00 AM - 04:00 PM",
            location: "Varanasi Slum Communities",
            description: "Our annual comprehensive education drive focusing on enrollment of children in schools and distribution of educational materials. This year we aim to reach 300+ children across 10 slum areas.",
            participants: "300+ Children Expected",
            category: "Education",
            status: "Registration Open",
            image: "/images/IMG-20250605-WA0406.jpg",
            priority: "high",
            registrationLink: "#"
        },
        {
            id: 2,
            title: "Women Empowerment Workshop Series",
            date: "2024-07-20",
            time: "10:00 AM - 02:00 PM",
            location: "Community Center, Varanasi",
            description: "Three-day workshop series on self-help group formation, leadership skills, and income generation activities. Building decision-making capacity among women in slum communities.",
            participants: "50 Women",
            category: "Empowerment",
            status: "Upcoming",
            image: "/images/IMG-20250605-WA0408.jpg",
            priority: "medium",
            registrationLink: "#"
        },
        {
            id: 3,
            title: "Health and Hygiene Awareness Camp",
            date: "2024-07-10",
            time: "08:00 AM - 05:00 PM",
            location: "Multiple Villages, Varanasi",
            description: "Free health checkups, awareness programs on hygiene practices, and distribution of hygiene kits. Collaboration with local health departments for comprehensive healthcare access.",
            participants: "200+ Families",
            category: "Health",
            status: "Upcoming",
            image: "/images/IMG-20250605-WA0409.jpg",
            priority: "high",
            registrationLink: "#"
        },
        {
            id: 4,
            title: "Vocational Training Program Launch",
            date: "2024-09-01",
            time: "11:00 AM - 01:00 PM",
            location: "UKKFT Training Center",
            description: "Launch of new vocational training program for unemployed youth and women. Focus on practical skills that lead to sustainable employment and income generation.",
            participants: "100 Participants",
            category: "Training",
            status: "Applications Open",
            image: "/images/IMG-20250605-WA0410.jpg",
            priority: "medium",
            registrationLink: "#"
        }
    ];

    const pastEvents = [
        {
            id: 1,
            title: "Children Development Program 2023",
            date: "2023-11-25",
            location: "Varanasi Slum Areas",
            description: "Successfully organized children into groups and provided personality development lessons. Created positive environments for slum children through recreational activities and social exposure programs.",
            participants: "150 Children",
            category: "Development",
            impact: "Improved confidence and social skills among participants",
            image: "/images/IMG-20250605-WA0411.jpg",
            rating: 4.8,
            testimonial: "The program helped build confidence in our children"
        },
        {
            id: 2,
            title: "Community Health Workshop 2023",
            date: "2023-10-15",
            location: "Community Centers, Varanasi",
            description: "Organized community meeting focused on health, hygiene, and viral disease prevention. Distributed hygiene kits and conducted comprehensive awareness sessions.",
            participants: "40 Families",
            category: "Health",
            impact: "Increased awareness about preventive healthcare practices",
            image: "/images/IMG-20250605-WA0412.jpg",
            rating: 4.6,
            testimonial: "Essential health information was shared effectively"
        },
        {
            id: 3,
            title: "Self-Defence Training Program 2023",
            date: "2023-09-20",
            location: "UKKFT Training Ground",
            description: "Conducted self-defence training programs for children and youth. Empowered participants with confidence and safety skills for personal protection.",
            participants: "75 Youth",
            category: "Training",
            impact: "Enhanced self-confidence and safety awareness",
            image: "/images/IMG-20250605-WA0413.jpg",
            rating: 4.9,
            testimonial: "Excellent training that built confidence and skills"
        },
        {
            id: 4,
            title: "Educational Excellence Awards 2023",
            date: "2023-03-15",
            location: "UKKFT Premises",
            description: "Recognition ceremony for children from our programs who achieved excellent academic results. Celebrated educational achievements and motivated continued learning.",
            participants: "50 Children",
            category: "Recognition",
            impact: "Motivated children to continue their educational journey",
            image: "/images/IMG-20250605-WA0414.jpg",
            rating: 5.0,
            testimonial: "Inspiring event that motivated our children greatly"
        },
        {
            id: 5,
            title: "8th Foundation Day Celebration",
            date: "2023-12-01",
            location: "Varanasi",
            description: "Celebrated 8 years of service to underprivileged children. Showcased achievements, shared impact stories, and renewed commitment for future work.",
            participants: "200+ Community Members",
            category: "Celebration",
            impact: "Strengthened community bonds and renewed partnerships",
            image: "/images/IMG-20250605-WA0415.jpg",
            rating: 4.7,
            testimonial: "Wonderful celebration of our collective achievements"
        },
        {
            id: 6,
            title: "Women Self-Help Group Formation",
            date: "2023-08-10",
            location: "Various Slum Areas",
            description: "Successfully organized women into self-help groups and provided leadership training. Many women have now taken on decision-making roles in their communities.",
            participants: "100 Women",
            category: "Empowerment",
            impact: "Formed 10 active self-help groups",
            image: "/images/IMG-20250605-WA0416.jpg",
            rating: 4.8,
            testimonial: "Life-changing experience for women in our community"
        }
    ];

    const regularActivities = [
        {
            title: "Daily Education Classes",
            frequency: "Monday - Friday",
            time: "10:00 AM - 02:00 PM",
            description: "Regular education classes for slum children to improve literacy and decision-making capacity",
            location: "Multiple Centers",
            icon: <FaGraduationCap />,
            color: "blue",
            participants: "150+ Children Daily"
        },
        {
            title: "Weekly Health Checkups",
            frequency: "Every Saturday",
            time: "09:00 AM - 12:00 PM",
            description: "Basic health checkups and health awareness sessions for community members",
            location: "Community Health Centers",
            icon: <FaHeartbeat />,
            color: "red",
            participants: "40+ Families Weekly"
        },
        {
            title: "Monthly Vocational Training",
            frequency: "First Weekend of Month",
            time: "10:00 AM - 04:00 PM",
            description: "Skills development programs for women and unemployed youth",
            location: "Training Centers",
            icon: <FaHandsHelping />,
            color: "green",
            participants: "30+ People Monthly"
        },
        {
            title: "Quarterly Community Meetings",
            frequency: "Every 3 Months",
            time: "11:00 AM - 02:00 PM",
            description: "Community engagement sessions to assess needs and plan future activities",
            location: "Community Halls",
            icon: <FaUsers />,
            color: "purple",
            participants: "100+ Community Members"
        }
    ];

    const eventCategories = ['All', 'Education', 'Health', 'Training', 'Empowerment', 'Development', 'Recognition', 'Celebration'];

    const filterEventsByCategory = (events) => {
        if (selectedCategory === 'All') return events;
        return events.filter(event => event.category === selectedCategory);
    };



    const getCategoryBadgeClass = (category) => {
        const classes = {
            Education: 'bg-blue-100 text-blue-700',
            Health: 'bg-red-100 text-red-700',
            Training: 'bg-green-100 text-green-700',
            Empowerment: 'bg-orange-100 text-orange-700',
            Development: 'bg-purple-100 text-purple-700',
            Recognition: 'bg-pink-100 text-pink-700',
            Celebration: 'bg-indigo-100 text-indigo-700'
        };
        return classes[category] || 'bg-gray-100 text-gray-700';
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <motion.section
                className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden"
                initial="hidden"
                animate="visible"
            >
                <div className="absolute inset-0">
                    <img
                        src="/images/IMG-20250605-WA0380.jpg"
                        alt="Ummeed Ki Kiran Foundation Events"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-green-900/90 via-blue-800/80 to-purple-900/90"></div>
                </div>

                <div className="relative z-10 max-w-6xl mx-auto px-4 text-center text-white">
                    <motion.div variants={staggerContainer}>
                        <motion.h1
                            className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-green-200 bg-clip-text text-transparent"
                            variants={fadeInUp}
                        >
                            Events & Activities
                        </motion.h1>
                        <motion.p
                            className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed"
                            variants={fadeInUp}
                        >
                            Join us in our mission to transform lives through education, health, and empowerment programs in Varanasi
                        </motion.p>
                        <motion.div
                            className="flex flex-wrap justify-center gap-6"
                            variants={fadeInUp}
                        >
                            <div className="bg-white/20 backdrop-blur-md px-6 py-3 rounded-full border border-white/30 flex items-center gap-3">
                                <FaCalendarAlt className="text-green-400 text-xl" />
                                <span className="font-semibold">Regular Events</span>
                            </div>
                            <div className="bg-white/20 backdrop-blur-md px-6 py-3 rounded-full border border-white/30 flex items-center gap-3">
                                <FaUsers className="text-blue-400 text-xl" />
                                <span className="font-semibold">Community Focus</span>
                            </div>
                            <div className="bg-white/20 backdrop-blur-md px-6 py-3 rounded-full border border-white/30 flex items-center gap-3">
                                <FaHandsHelping className="text-purple-400 text-xl" />
                                <span className="font-semibold">Impact Driven</span>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>

            {/* Navigation Tabs */}
            <motion.section
                className="py-12 bg-gradient-to-br from-gray-50 to-green-50"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-4xl mx-auto px-4">
                    <motion.div
                        className="flex flex-wrap justify-center gap-4"
                        variants={staggerContainer}
                    >
                        <motion.button
                            className={`px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 flex items-center gap-3 ${activeTab === 'upcoming'
                                ? 'bg-green-600 text-white shadow-lg'
                                : 'bg-white text-gray-600 hover:bg-green-50 hover:text-green-600'
                                }`}
                            onClick={() => setActiveTab('upcoming')}
                            variants={scaleIn}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <FaCalendarAlt />
                            <span>Upcoming Events</span>
                        </motion.button>
                        <motion.button
                            className={`px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 flex items-center gap-3 ${activeTab === 'past'
                                ? 'bg-blue-600 text-white shadow-lg'
                                : 'bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                                }`}
                            onClick={() => setActiveTab('past')}
                            variants={scaleIn}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <FaBookmark />
                            <span>Past Events</span>
                        </motion.button>
                        <motion.button
                            className={`px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 flex items-center gap-3 ${activeTab === 'regular'
                                ? 'bg-purple-600 text-white shadow-lg'
                                : 'bg-white text-gray-600 hover:bg-purple-50 hover:text-purple-600'
                                }`}
                            onClick={() => setActiveTab('regular')}
                            variants={scaleIn}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <FaClock />
                            <span>Regular Activities</span>
                        </motion.button>
                    </motion.div>
                </div>
            </motion.section>

            {/* Category Filter */}
            {(activeTab === 'upcoming' || activeTab === 'past') && (
                <motion.section
                    className="py-8 bg-white"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                >
                    <div className="max-w-6xl mx-auto px-4">
                        <motion.div
                            className="flex flex-wrap justify-center gap-3"
                            variants={staggerContainer}
                        >
                            {eventCategories.map((category) => (
                                <motion.button
                                    key={category}
                                    className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${selectedCategory === category
                                        ? 'bg-indigo-600 text-white shadow-lg'
                                        : 'bg-gray-100 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600'
                                        }`}
                                    onClick={() => setSelectedCategory(category)}
                                    variants={scaleIn}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    {category}
                                </motion.button>
                            ))}
                        </motion.div>
                    </div>
                </motion.section>
            )}

            {/* Events Content */}
            <AnimatePresence mode="wait">
                {activeTab === 'upcoming' && (
                    <motion.section
                        className="py-20 bg-gray-50"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -50 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="max-w-7xl mx-auto px-4">
                            <motion.div className="text-center mb-12" variants={staggerContainer}>
                                <motion.h2 className="text-4xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
                                    Upcoming Events
                                </motion.h2>
                                <motion.p className="text-xl text-gray-600" variants={fadeInUp}>
                                    Join us in creating positive change in our community
                                </motion.p>
                            </motion.div>

                            <motion.div
                                className="grid grid-cols-1 lg:grid-cols-2 gap-8"
                                variants={staggerContainer}
                                key={selectedCategory}
                            >
                                {filterEventsByCategory(upcomingEvents).map((event) => (
                                    <motion.div
                                        key={event.id}
                                        className="bg-white rounded-2xl shadow-lg overflow-hidden group"
                                        variants={slideInFromBottom}
                                        whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <div className="relative overflow-hidden h-64">
                                            <img
                                                src={event.image}
                                                alt={event.title}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                            />
                                            <div className="absolute top-4 left-4">
                                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryBadgeClass(event.category)}`}>
                                                    {event.category}
                                                </span>
                                            </div>
                                            <div className="absolute top-4 right-4">
                                                <span className={`px-3 py-1 text-white text-sm font-medium rounded-full flex items-center gap-2 ${event.priority === 'high' ? 'bg-red-600' : 'bg-orange-600'
                                                    }`}>
                                                    {event.priority === 'high' && <FaStar className="text-xs" />}
                                                    {event.status}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="p-8">
                                            <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-green-600 transition-colors">
                                                {event.title}
                                            </h3>

                                            <div className="grid grid-cols-2 gap-4 mb-6 text-gray-600">
                                                <div className="flex items-center gap-2">
                                                    <FaCalendarAlt className="text-green-600" />
                                                    <span className="text-sm">{new Date(event.date).toLocaleDateString()}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <FaClock className="text-blue-600" />
                                                    <span className="text-sm">{event.time}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <FaMapMarkerAlt className="text-red-600" />
                                                    <span className="text-sm">{event.location}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <FaUsers className="text-purple-600" />
                                                    <span className="text-sm">{event.participants}</span>
                                                </div>
                                            </div>

                                            <p className="text-gray-600 leading-relaxed mb-6">
                                                {event.description}
                                            </p>

                                            <motion.a
                                                href={event.registrationLink}
                                                className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-full font-medium hover:bg-green-700 transition-colors"
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                            >
                                                <FaExternalLinkAlt />
                                                Register Now
                                            </motion.a>
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </motion.section>
                )}

                {activeTab === 'past' && (
                    <motion.section
                        className="py-20 bg-white"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -50 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="max-w-7xl mx-auto px-4">
                            <motion.div className="text-center mb-12" variants={staggerContainer}>
                                <motion.h2 className="text-4xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
                                    Past Events & Achievements
                                </motion.h2>
                                <motion.p className="text-xl text-gray-600" variants={fadeInUp}>
                                    Celebrating our successful initiatives and their impact
                                </motion.p>
                            </motion.div>

                            <motion.div
                                className="grid grid-cols-1 lg:grid-cols-2 gap-8"
                                variants={staggerContainer}
                                key={selectedCategory}
                            >
                                {filterEventsByCategory(pastEvents).map((event) => (
                                    <motion.div
                                        key={event.id}
                                        className="bg-white rounded-2xl shadow-lg overflow-hidden group border border-gray-100"
                                        variants={slideInFromBottom}
                                        whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <div className="relative overflow-hidden h-64">
                                            <img
                                                src={event.image}
                                                alt={event.title}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                            />
                                            <div className="absolute top-4 left-4">
                                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryBadgeClass(event.category)}`}>
                                                    {event.category}
                                                </span>
                                            </div>
                                            <div className="absolute top-4 right-4">
                                                <span className="px-3 py-1 bg-green-600 text-white text-sm font-medium rounded-full flex items-center gap-2">
                                                    <FaCheckCircle className="text-xs" />
                                                    Completed
                                                </span>
                                            </div>
                                            <div className="absolute bottom-4 right-4">
                                                <div className="bg-white/90 px-3 py-1 rounded-full flex items-center gap-2">
                                                    <FaStar className="text-yellow-500 text-sm" />
                                                    <span className="text-sm font-medium">{event.rating}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-8">
                                            <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-blue-600 transition-colors">
                                                {event.title}
                                            </h3>

                                            <div className="grid grid-cols-2 gap-4 mb-6 text-gray-600">
                                                <div className="flex items-center gap-2">
                                                    <FaCalendarAlt className="text-blue-600" />
                                                    <span className="text-sm">{new Date(event.date).toLocaleDateString()}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <FaMapMarkerAlt className="text-red-600" />
                                                    <span className="text-sm">{event.location}</span>
                                                </div>
                                                <div className="flex items-center gap-2 col-span-2">
                                                    <FaUsers className="text-purple-600" />
                                                    <span className="text-sm">{event.participants}</span>
                                                </div>
                                            </div>

                                            <p className="text-gray-600 leading-relaxed mb-4">
                                                {event.description}
                                            </p>

                                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <FaTrophy className="text-green-600" />
                                                    <span className="font-semibold text-green-800">Impact Achieved:</span>
                                                </div>
                                                <p className="text-green-700">{event.impact}</p>
                                            </div>

                                            {event.testimonial && (
                                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                    <p className="text-blue-700 italic">"{event.testimonial}"</p>
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </motion.section>
                )}

                {activeTab === 'regular' && (
                    <motion.section
                        className="py-20 bg-gradient-to-br from-gray-50 to-purple-50"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -50 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="max-w-7xl mx-auto px-4">
                            <motion.div className="text-center mb-12" variants={staggerContainer}>
                                <motion.h2 className="text-4xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
                                    Regular Activities
                                </motion.h2>
                                <motion.p className="text-xl text-gray-600" variants={fadeInUp}>
                                    Our ongoing programs that create consistent impact in the community
                                </motion.p>
                            </motion.div>

                            <motion.div
                                className="grid grid-cols-1 md:grid-cols-2 gap-8"
                                variants={staggerContainer}
                            >
                                {regularActivities.map((activity, index) => (
                                    <motion.div
                                        key={index}
                                        className="bg-white rounded-2xl shadow-lg p-8 group"
                                        variants={slideInFromBottom}
                                        whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)", scale: 1.02 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <div className="flex items-start gap-6">
                                            <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl group-hover:scale-110 transition-transform ${activity.color === 'blue' ? 'bg-blue-600' :
                                                activity.color === 'red' ? 'bg-red-600' :
                                                    activity.color === 'green' ? 'bg-green-600' :
                                                        activity.color === 'purple' ? 'bg-purple-600' : 'bg-gray-600'
                                                }`}>
                                                {activity.icon}
                                            </div>

                                            <div className="flex-1">
                                                <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-purple-600 transition-colors">
                                                    {activity.title}
                                                </h3>

                                                <div className="grid grid-cols-1 gap-3 mb-6 text-gray-600">
                                                    <div className="flex items-center gap-2">
                                                        <FaCalendarAlt className="text-purple-600" />
                                                        <span className="font-medium">{activity.frequency}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <FaClock className="text-blue-600" />
                                                        <span>{activity.time}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <FaMapMarkerAlt className="text-red-600" />
                                                        <span>{activity.location}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <FaUsers className="text-green-600" />
                                                        <span>{activity.participants}</span>
                                                    </div>
                                                </div>

                                                <p className="text-gray-600 leading-relaxed">
                                                    {activity.description}
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </motion.section>
                )}
            </AnimatePresence>
        </div>
    );
};

export default EventsPage; 